import type { EventSummary } from '../data/types'
import { deriveStatus, entryLimitAndCount } from '../data/registrationStatus'
import { formatEventDateRange } from '../data/format'
import { NoFlyerTile } from './NoFlyerTile'
import { StatusBadge, CtaButton } from './StatusBadge'
import styles from './EventCard.module.css'

/** Mobile list card, Main.dc.html's month-grouped event card: flyer + title/track/city on top,
 *  status + CTA footer below a divider. */
export function EventListCard({ event, canSignUp }: { event: EventSummary; canSignUp: boolean }) {
  const href = `/events/displayEvent.cfm?ID=${event.id}`
  const status = deriveStatus(event)

  return (
    <article className={styles.mobileCard}>
      <div className={styles.mobileCardTop}>
        <a href={href} aria-label={`View the ${event.name} flyer`} className={styles.mobileFlyerLink}>
          {event.flyerUrl ? (
            <>
              <img
                src={event.flyerUrl}
                alt={`${event.name} flyer - ${formatEventDateRange(event.startDT, event.endDT)} - ${event.trackName}`}
                className={styles.mobileFlyerImg}
              />
              <span className={styles.mobileFlyerExpand} aria-hidden="true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              </span>
            </>
          ) : (
            <NoFlyerTile id={event.id} name={event.name} raceType={event.raceType} caption="No flyer posted" />
          )}
        </a>
        <div className={styles.mobileInfo}>
          <div className={styles.mobileDates}>{formatEventDateRange(event.startDT, event.endDT)}</div>
          <a href={href} className={styles.mobileTitle}>
            {event.name}
          </a>
          <div className={styles.mobileTrack}>{event.trackName}</div>
          <div className={styles.mobileCity}>
            {event.city}, {event.state}
          </div>
          <div className={styles.mobileStatusRow}>
            <StatusBadge status={status} />
          </div>
        </div>
      </div>
      {canSignUp ? (
        <div className={styles.mobileFooter}>
          <span className={styles.mobileMeta}>{entryMeta(event, canSignUp)}</span>
          <CtaButton status={status} href={href} canSignUp={canSignUp} />
        </div>
      ) : null}
    </article>
  )
}

/** Entry count/limit is racer-facing info - logged-out visitors and tracks get nothing here
 *  rather than a class-count stand-in (callers skip rendering the meta span entirely when this
 *  comes back empty). */
export function entryMeta(event: EventSummary, canSignUp: boolean): string {
  if (!canSignUp) return ''
  const { limit, count } = entryLimitAndCount(event)
  if (limit > 0) return `${count} / ${limit}`
  if (count > 0) return `${count} entered`
  return `${event.classCount} class${event.classCount === 1 ? '' : 'es'}`
}
